import React from "react";
import {
    Button,
    ButtonType,
    CellAction,
    CellModel,
    Checkbox,
    ColumnDefinition,
    ColumnType,
    Grid,
    GridHoveringType,
    GridModel,
    GridOddType,
    IconSize,
    Inline,
    JustifyContent,
    RowModel, TextInput,
    ToolbarContainer,
    ToolbarRow
} from "@renta-apps/athenaeum-react-components";
import {ActionType, BaseComponent, ch, IBaseComponent, TextAlign} from "@renta-apps/athenaeum-react-common";
import User from "@/models/server/User";
import {IPagedList, SortDirection, Utility} from "@renta-apps/athenaeum-toolkit";
import MassProduct from "@/models/server/MassProduct";
import RentaToolsConstants from "@/helpers/RentaToolsConstants";
import ListMassProductsRequest from "@/models/server/requests/ListMassProductsRequest";
import SaveMassProductRequest from "@/models/server/requests/SaveMassProductRequest";
import SaveMassProductResponse from "@/models/server/responses/SaveMassProductResponse";
import Debouncer from "@/helpers/Debouncer";
import MassProductsController from "@/pages/MassProductsController";
import Localizer from "@/localization/Localizer";

import styles from "@/pages/MassProductsPage/MassProductsData/MassProductsData.module.scss";

interface IMassProductsDataProps  {
    reloadMassProductsMappings(sender: IBaseComponent): Promise<void>;
}

interface IMassProductsDataState {
    showDeleted: boolean;
    searchTerm: string | null;
}

export default class MassProductsData extends BaseComponent<IMassProductsDataProps, IMassProductsDataState> {

    state: IMassProductsDataState = {
        showDeleted: false,
        searchTerm: null,
    };

    public getTitle(): string {
        return Localizer.massProductsDataTitle;
    }

    private readonly _massProductsGridRef: React.RefObject<Grid<MassProduct>> = React.createRef();
    private readonly _searchDebouncer: Debouncer = new Debouncer();

    private readonly _massProductsColumns: ColumnDefinition[] = [
        {
            header: "#",
            accessor: "#",
            minWidth: 50,
            noWrap: true,
            className: "grey",
            textAlign: TextAlign.Center
        },
        {
            header: Localizer.massProductsDataProductGroupIdLanguageItemName,
            name: "productGroupId",
            accessor: nameof<MassProduct>(o => o.productGroupId),
            minWidth: 200,
            type: ColumnType.Text,
            editable: true,
            sorting: true,
            noWrap: true,
            reRenderRow: true,
            settings: {
                required: true,
                maxLength: RentaToolsConstants.dbKeyLength,
            }
        },
        {
            header: Localizer.massProductsDataProductTypeLanguageItemName,
            name: "productType",
            accessor: nameof<MassProduct>(d => d.productType),
            minWidth: 200,
            type: ColumnType.Text,
            editable: true,
            sorting: true,
            noWrap: true,
            settings: {
                maxLength: RentaToolsConstants.dbKeyLength,
            }
        },
        {
            header: Localizer.massProductsDataReportDefinitionNameLanguageItemName,
            name: "reportDefinitionName",
            accessor: nameof<MassProduct>(d => d.reportDefinitionName),
            minWidth: 200,
            type: ColumnType.Custom,
            sorting: true,
            noWrap: true,
        },
        {
            stretch: true,
            minWidth: "6rem",
            removable: false,
            init: (cell) => this.initMassProductOperationsAsync(cell),
            actions: [
                {
                    name: "save",
                    title: Localizer.genericSaveLanguageItemName,
                    icon: "far save",
                    type: ActionType.Create,
                    callback: async (cell, action) => await this.processMassProductOperationAsync(cell, action)
                },
                {
                    name: "cancel",
                    title: Localizer.genericCancelLanguageItemName,
                    icon: "far ban",
                    type: ActionType.Delete,
                    right: true,
                    callback: async (cell, action) => await this.processMassProductOperationAsync(cell, action)
                },
                {
                    name: "delete",
                    title: Localizer.genericRemoveLanguageItemName,
                    icon: "far trash-alt",
                    type: ActionType.Delete,
                    right: true,
                    confirm: (cell) => this.getDeleteConfirmation(cell),
                    callback: async (cell, action) => await this.processMassProductOperationAsync(cell, action)
                },
                {
                    name: "restore",
                    title: Localizer.genericRestoreLanguageItemName,
                    icon: "far undo-alt",
                    type: ActionType.Create,
                    right: true,
                    callback: async (cell, action) => await this.processMassProductOperationAsync(cell, action)
                }
            ]
        }
    ];

    private get massProductsGrid(): GridModel<MassProduct> {
        return this._massProductsGridRef.current!.model;
    }

    private get newRowAlreadyExists(): boolean {
        return (this.massProductsGrid.rows.some(row => !row.deleted && !row.model.id));
    }

    private initRow(row: RowModel<MassProduct>): void {
        const model: MassProduct = row.model;

        const isNew: boolean = (!model.id);
        const isValid: boolean = this.isValid(model);
        row.deleted = model.deleted;

        row.className = (!isValid)
            ? "danger"
            : (isNew)
                ? "bg-processed"
                : "";
    }

    private isValid(massProduct: MassProduct): boolean {
        const productGroupId = massProduct.productGroupId.trim();

        return (!!productGroupId);
    }

    private getDeleteConfirmation(cell: CellModel<MassProduct>): string {
        const model: MassProduct = cell.model;
        const isNew: boolean = !model.id;
        return (isNew)
            ? null as any
            : Utility.format(Localizer.massProductsDataDeleteConfirmation, model.productGroupId);
    }

    private async getMassProductsAsync(
        pageNumber: number,
        pageSize: number,
        sortColumnName: string | null,
        sortDirection: SortDirection | null
    ): Promise<IPagedList<MassProduct>> {

        const request = new ListMassProductsRequest();
        request.pageNumber = pageNumber;
        request.pageSize = pageSize;
        request.showDeleted = this.state.showDeleted;
        request.searchTerm = this.state.searchTerm;
        request.sortColumnName = sortColumnName;
        request.sortDirection = sortDirection;

        return await MassProductsController.listMassProductsAsync(request);
    }

    private async initMassProductOperationsAsync(cell: CellModel<MassProduct>): Promise<void> {

        const model: MassProduct = cell.row.model;

        const modified: boolean = cell.row.modified;
        const deleted: boolean = cell.row.deleted;
        const isValid: boolean = this.isValid(model);
        const isNew: boolean = !model.id;

        const saveAction: CellAction<MassProduct> = cell.actions[0];
        const cancelAction: CellAction<MassProduct> = cell.actions[1];
        const deleteAction: CellAction<MassProduct> = cell.actions[2];
        const restoreAction: CellAction<MassProduct> = cell.actions[3];

        saveAction.visible = (modified) && (isValid);
        cancelAction.visible = (modified) && (!isNew);
        deleteAction.visible = (!deleted) && ((!modified) || (isNew));
        restoreAction.visible = (deleted);
    }

    private async processMassProductOperationAsync(cell: CellModel<MassProduct>, action: CellAction<MassProduct>): Promise<void> {

        await ch.hideAlertAsync();

        const model: MassProduct = cell.model;
        const isNew: boolean = (!model.id);

        if (action.action.name === "save") {

            const request = new SaveMassProductRequest();
            request.productGroupId = model.productGroupId;
            request.productType = model.productType;

            if (isNew) {
                const response: SaveMassProductResponse = await MassProductsController.saveMassProductAsync(request);

                if (response.massProductAlreadyExists) {
                    await ch.alertErrorAsync(Utility.format(Localizer.massProductsDataMassProductAlreadyExists, model.productGroupId), true);
                    return;
                }

                cell.row.model = response.massProduct!;

                await cell.row.saveAsync();

            } else {
                request.massProductId = model.id;

                const response: SaveMassProductResponse = await MassProductsController.saveMassProductAsync(request);

                if (response.massProductAlreadyExists) {
                    await ch.alertErrorAsync(Utility.format(Localizer.massProductsDataMassProductAlreadyExists, model.productGroupId), true);
                    return;
                }

                cell.row.model = response.massProduct!;

                await cell.row.bindAsync();
            }

            await cell.row.bindAsync();

        } else if (action.action.name === "cancel") {

            await cell.row.cancelAsync();

        } else if (action.action.name === "delete") {

            model.deleted = true;
            model.deletedAt = new Date();
            model.deletedBy = ch.getUser<User>();

            if (isNew) {
                await cell.grid.deleteAsync(cell.row.index);
            } else {

                const removedPermanently: boolean = await MassProductsController.deleteMassProductAsync(model.id);

                if (removedPermanently) {
                    await cell.grid.deleteAsync(cell.row.index);
                    await ch.alertMessageAsync(Utility.format(Localizer.massProductsDataAlertMessageMassProductDeletedPermanently, model.productGroupId), true);
                } else {
                    model.reportDefinitionId = null;
                    model.reportDefinitionName = null;

                    await cell.row.setDeletedAsync(true);
                    await ch.alertMessageAsync(Utility.format(Localizer.massProductsDataAlertMessageMassProductMarkedAsDeleted, model.productGroupId), true);
                }
            }

        } else if (action.action.name === "restore") {

            const restoreOnServer: boolean = !isNew;

            if (restoreOnServer) {

                await MassProductsController.restoreMassProductAsync(model.id);

                model.deleted = false;
                model.deletedAt = null;
                model.deletedBy = null;
            }

            await cell.row.setDeletedAsync(false);
        }

        await this.props.reloadMassProductsMappings(this);
    }

    private async addMassProductAsync(): Promise<void> {
        if (!this.newRowAlreadyExists) {
            const massProduct = new MassProduct();

            const newRows: RowModel<MassProduct>[] = await this.massProductsGrid.insertAsync(0, massProduct);

            const newRow: RowModel<MassProduct> = newRows[0];
            const nameCell: CellModel<MassProduct> = newRow.get("productGroupId");

            await nameCell.editAsync(true);
        }
    }

    private async onChangeSearchTermAsync(searchTerm: string | null): Promise<void> {
        if (this.state.searchTerm !== searchTerm) {
            await this.setState({searchTerm: searchTerm});

            this._searchDebouncer.debounce(
                async () => {
                    await this.reloadAsync();
                },
                500
            );
        }
    }

    private async setShowDeletedAsync(showDeleted: boolean): Promise<void> {
        await this.setState({showDeleted});
        await this.reloadAsync();
    }

    public async reloadAsync(): Promise<void> {
        await this.massProductsGrid.reloadAsync();
    }

    public render(): React.ReactNode {
        return (
            <div className={styles.massProductsData}>

                <ToolbarContainer className={styles.toolbar}>

                    <ToolbarRow justify={JustifyContent.SpaceBetween}>

                        <TextInput clearButton
                                   width={"60ch"}
                                   value={this.state.searchTerm}
                                   placeholder={Localizer.massProductsDataSearch}
                                   onChange={(_, value) => this.onChangeSearchTermAsync(value)}
                        />

                        <Inline justify={JustifyContent.End}>

                            <Checkbox inline
                                      id="showDeletedMassProducts"
                                      label={Localizer.genericShowDeleted}
                                      value={this.state.showDeleted}
                                      onChange={async (_, value) => await this.setShowDeletedAsync(value)}
                            />

                            <Button id="reloadMassProducts"
                                    className="ml-1"
                                    title={Localizer.genericReload}
                                    icon={{name: "far history", size: IconSize.Large}}
                                    type={ButtonType.Info}
                                    onClick={async () => await this.reloadAsync()}
                            />

                            <Button id="addMassProduct"
                                    icon={{name: "plus", size: IconSize.Large}}
                                    type={ButtonType.Orange}
                                    title={Localizer.massProductsDataAdd}
                                    onClick={async () => await this.addMassProductAsync()}
                            />

                        </Inline>

                    </ToolbarRow>

                </ToolbarContainer>

                <Grid version2Styles
                      ref={this._massProductsGridRef}
                      id="massProductsGrid"
                      className={styles.grid}
                      pagination={10}
                      hovering={GridHoveringType.EditableCell}
                      odd={GridOddType.None}
                      minWidth="auto"
                      columns={this._massProductsColumns}
                      noDataText={Localizer.genericNoData}
                      initRow={(row) => this.initRow(row)}
                      fetchData={async (_, pageNumber, pageSize, sortColumnName, sortDirection) => await this.getMassProductsAsync(pageNumber, pageSize, sortColumnName, sortDirection)}
                />

            </div>
        );
    }
}